/**
 * OWNER-DAILY-EOD-REPORT-V2 -- the PRIMARY business table: treatments.
 *
 * COLUMNS ARE CONFIG-BACKED AND ROLE-SEEDED (founder ruling 2026-08-19).
 * This component holds NO column literal of its own: the caller passes the
 * lens the server resolved from org config. The Partha Owner seed is
 * Treatment + Revenue; Units is available through the same config key and
 * is OFF for Owner. Trend is absent from the P1 vocabulary because the
 * current read path carries no prior-period object -- a Trend column would
 * have nothing truthful to print, so it is not rendered rather than filled
 * with a placeholder. OWNER-CONSOLE-COMPARISON-PERIOD-V1 adds it.
 *
 * ONE REUSABLE PRIMITIVE, NOT A TABLE PER ROLE: the next role package
 * passes its own resolved lens and its own scope to this same component.
 *
 * THE PAGE NEVER GROWS WITH THE DATA (visual contract s.8a): rows that
 * carry activity render inline, capped, with the cap stated in the header;
 * the FULL set opens in a DrawerShell, never an inline expansion.
 *
 * UNMAPPED MONEY IS NOT A TREATMENT: it never renders as a treatment row,
 * a revenue-share row, or a trend row, and it is never silently folded
 * into "Other". It appears once as a compact mapping call-out and once in
 * the compact reconciliation line, and detailed mapping stays on the
 * existing configuration surface.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  ConsoleProductRowWire,
  ConsoleTreatmentColumn,
  ConsoleUnmappedItemWire,
} from '@/dashboard/types';
import {
  DataTable,
  DrawerShell,
  EmptyState,
  QuietAction,
  SectionContainer,
  type DataTableColumn,
} from '@/dashboard/ui';

import { inrMinor, type ReportScope } from './report-scope';

/** Inline rows before the rest moves behind the drawer (contract s.8a
 *  caps an inline list at 3-5 rows with the cap stated in the header). */
export const TREATMENT_INLINE_CAP = 5;

/** The configuration surface that owns catalog-to-row mapping. */
export const TREATMENT_MAPPING_HREF = '/dashboard/config/catalog-rows';

/** The lens used when the caller supplies none. A column set is PRESENTATION
 *  config, never business truth, so an absent one renders the default
 *  columns -- it must never take the report down. Mirrors the server's own
 *  CONSOLE_TREATMENT_COLUMN_FALLBACK (lib/owner-money-console.ts). */
export const TREATMENT_COLUMN_FALLBACK: readonly ConsoleTreatmentColumn[] = [
  'treatment',
  'revenue',
];

export interface TreatmentRevenueSectionProps {
  readonly scope: ReportScope;
  /** Every configured row, in configured order. */
  readonly rows: readonly ConsoleProductRowWire[];
  /** The resolved column lens for this reader's role. An absent or empty
   *  lens falls back to TREATMENT_COLUMN_FALLBACK rather than rendering a
   *  headless table or throwing. */
  readonly columns?: readonly ConsoleTreatmentColumn[] | null;
  /** Catalog items that carry activity but map to no row. */
  readonly unmappedItems: readonly ConsoleUnmappedItemWire[];
  /** Total collected in the period -- the reconciliation's right-hand side. */
  readonly collectedMinorUnits: number;
  /** Opens the visits behind one treatment row; omit to render inert. */
  readonly onDrillRow?: (rowKey: string) => void;
  readonly testId?: string;
}

/**
 * The clinic-facing treatment name (founder closeout 2026-08-19, item 1).
 *
 * CONFIGURED LABELS WIN. The route resolves rows from org config, and a
 * configured row arrives with provisional = false -- its label is the
 * authoritative clinic-facing name and renders verbatim. This function
 * never replaces a real configured label with fallback copy.
 *
 * A row still marked PROVISIONAL is one the authoritative configuration has
 * not named yet. Its seeded label carries internal configuration vocabulary
 * ("... (config name pending)"), which is engineering register and must
 * never reach a clinic user. Such a row renders the repo's established
 * clinic-facing truth tag instead -- the same 'Name pending' the doctor
 * surfaces use for an unresolved name (dashboard/doctor/doctor-consult-view
 * .ts). No treatment name is invented, and the row KEY, the catalog
 * mappings and the 19-row business definition are untouched.
 */
export function treatmentDisplayLabel(row: ConsoleProductRowWire): string {
  return row.provisional ? TREATMENT_NAME_PENDING : row.label;
}

/** The repo-approved clinic-facing tag for a name the config has not set. */
export const TREATMENT_NAME_PENDING = 'Name pending';

/** A row carries activity when it has visits or revenue in the period. */
export function hasActivity(row: ConsoleProductRowWire): boolean {
  return row.visitCount > 0 || row.revenueMinorUnits > 0;
}

/**
 * Activity first, then by revenue, then by the configured order. Pure and
 * exported so the render pins assert the ordering without a DOM.
 */
export function orderTreatmentRows(
  rows: readonly ConsoleProductRowWire[],
): ConsoleProductRowWire[] {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const activityDelta = Number(hasActivity(b.row)) - Number(hasActivity(a.row));
      if (activityDelta !== 0) return activityDelta;
      const revenueDelta = b.row.revenueMinorUnits - a.row.revenueMinorUnits;
      if (revenueDelta !== 0) return revenueDelta;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
}

/**
 * The reconciliation identity, composed from truth the wire already
 * carries -- never a second derivation of revenue. Mapped is what the
 * configured rows account for; the remainder is money the report can see
 * but cannot attribute to a row, and it is disclosed rather than guessed
 * into one.
 */
export function reconcileTreatmentRevenue(
  rows: readonly ConsoleProductRowWire[],
  collectedMinorUnits: number,
): {
  readonly mappedMinorUnits: number;
  readonly unmappedMinorUnits: number;
  readonly collectedMinorUnits: number;
} {
  const mapped = rows.reduce((sum, r) => sum + r.revenueMinorUnits, 0);
  return {
    mappedMinorUnits: mapped,
    unmappedMinorUnits: collectedMinorUnits - mapped,
    collectedMinorUnits,
  };
}

/** Normalize whatever the caller passed into a renderable lens. */
export function resolveLens(
  columns: readonly ConsoleTreatmentColumn[] | null | undefined,
): readonly ConsoleTreatmentColumn[] {
  return Array.isArray(columns) && columns.length > 0
    ? columns
    : TREATMENT_COLUMN_FALLBACK;
}

function buildColumns(
  lens: readonly ConsoleTreatmentColumn[],
  onDrillRow: ((rowKey: string) => void) | undefined,
): DataTableColumn<ConsoleProductRowWire>[] {
  const defs: Record<
    ConsoleTreatmentColumn,
    DataTableColumn<ConsoleProductRowWire>
  > = {
    treatment: {
      key: 'treatment',
      header: 'Treatment',
      render: (r) =>
        onDrillRow !== undefined && hasActivity(r) ? (
          <QuietAction size="sm" onClick={() => onDrillRow(r.key)}>
            {treatmentDisplayLabel(r)}
          </QuietAction>
        ) : (
          treatmentDisplayLabel(r)
        ),
    },
    revenue: {
      key: 'revenue',
      header: 'Revenue',
      align: 'end',
      render: (r) => inrMinor(r.revenueMinorUnits),
    },
    units: {
      key: 'units',
      header: 'Units',
      align: 'end',
      render: (r) => String(r.visitCount),
    },
  };
  return lens.map((key) => defs[key]);
}

/** The compact mapping call-out: a count and the money waiting on it --
 *  never a dump of catalog codes on the owner's report. */
function MappingCallout({
  unmappedItems,
  unmappedMinorUnits,
}: {
  readonly unmappedItems: readonly ConsoleUnmappedItemWire[];
  readonly unmappedMinorUnits: number;
}): JSX.Element | null {
  if (unmappedItems.length === 0) return null;
  const visits = unmappedItems.reduce((n, u) => n + u.acceptedItemCount, 0);
  return (
    <p className="omc-mapnote" data-testid="report-treatment-mapping">
      <b>
        {unmappedItems.length} treatment
        {unmappedItems.length === 1 ? '' : 's'} need
        {unmappedItems.length === 1 ? 's' : ''} mapping
      </b>
      {' \u00B7 '}
      {visits} accepted item{visits === 1 ? '' : 's'}
      {unmappedMinorUnits > 0
        ? ` \u00B7 ${inrMinor(unmappedMinorUnits)} awaiting mapping`
        : ''}
      {'. '}
      <Link to={TREATMENT_MAPPING_HREF} data-testid="report-treatment-mapping-link">
        Review mappings
      </Link>
    </p>
  );
}

export default function TreatmentRevenueSection({
  scope,
  rows,
  columns,
  unmappedItems,
  collectedMinorUnits,
  onDrillRow,
  testId = 'report-treatments',
}: TreatmentRevenueSectionProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const ordered = orderTreatmentRows(rows);
  const active = ordered.filter(hasActivity);
  const quiet = ordered.length - active.length;
  const inline = active.slice(0, TREATMENT_INLINE_CAP);
  const tableColumns = buildColumns(resolveLens(columns), onDrillRow);
  const reconciliation = reconcileTreatmentRevenue(rows, collectedMinorUnits);

  return (
    <SectionContainer
      title={`Treatments \u00B7 ${scope.periodLabel}`}
      count={active.length}
      scope={scope.label}
      stat={inrMinor(reconciliation.mappedMinorUnits)}
      defaultOpen
      testId={testId}
    >
      <MappingCallout
        unmappedItems={unmappedItems}
        unmappedMinorUnits={reconciliation.unmappedMinorUnits}
      />

      {active.length === 0 ? (
        <EmptyState
          title="No treatment revenue attributed yet"
          body={
            unmappedItems.length > 0
              ? 'Collected money is recorded, but no catalog item maps to a treatment row yet. Map them to see revenue by treatment.'
              : 'No treatment activity was recorded in this period.'
          }
        />
      ) : (
        <>
          <DataTable
            columns={tableColumns}
            rows={inline}
            rowKey={(r) => r.key}
            label="Treatments by revenue"
            testId="report-treatment-table"
          />
          {active.length > inline.length || quiet > 0 ? (
            <p className="omc-caption" data-testid="report-treatment-more">
              {quiet > 0
                ? `${quiet} treatment${quiet === 1 ? '' : 's'} with no activity. `
                : ''}
              <QuietAction
                size="sm"
                onClick={() => setDrawerOpen(true)}
                data-testid="report-treatment-showall"
              >
                {`Show all ${ordered.length}`}
              </QuietAction>
            </p>
          ) : null}
        </>
      )}

      <p className="omc-recon" data-testid="report-treatment-reconciliation">
        Mapped {inrMinor(reconciliation.mappedMinorUnits)}
        {' + '}Unmapped {inrMinor(reconciliation.unmappedMinorUnits)}
        {' = '}Collected {inrMinor(reconciliation.collectedMinorUnits)}
      </p>

      {drawerOpen ? (
        <DrawerShell
          eyebrow={scope.label}
          title={`All treatments \u00B7 ${scope.periodLabel}`}
          onClose={() => setDrawerOpen(false)}
          testId="report-treatment-drawer"
        >
          <DataTable
            columns={tableColumns}
            rows={ordered}
            rowKey={(r) => r.key}
            label="All treatments"
            testId="report-treatment-drawer-table"
          />
        </DrawerShell>
      ) : null}
    </SectionContainer>
  );
}
